/**
 * OperatorAnalytics Component
 *
 * Deep dive analytics view for a selected operator.
 * Shows progression chart, SOP breakdown, and recent assessments.
 */

import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  User,
  Building2,
  ClipboardCheck,
} from 'lucide-react';
import { useOperatorLookup } from '../../../hooks/useOperators';
import { useOperatorAnalytics, type OperatorAnalyticsData } from '../../../hooks/useAnalytics';
import { getScoreColor } from '../../../lib/score-colors';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';

interface OperatorAnalyticsProps {
  operatorId: string | null;
  period: number;
  onSelectOperator: (id: string | null) => void;
}

export function OperatorAnalytics({ operatorId, period, onSelectOperator }: OperatorAnalyticsProps) {
  const { data: operators, isLoading: operatorsLoading } = useOperatorLookup();
  const { data: analytics, isLoading: analyticsLoading } = useOperatorAnalytics(operatorId, period);

  // Operator selector
  if (!operatorId) {
    return (
      <div className="space-y-6">
        {/* Operator Selector */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-guild-mint/50">
              <User size={28} className="text-guild-green" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Select an Operator</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose an operator to view their detailed performance analytics.
            </p>
            <div className="mt-4 w-full max-w-sm">
              <select
                value=""
                onChange={(e) => onSelectOperator(e.target.value || null)}
                className="
                  w-full rounded-lg border border-gray-200 bg-white
                  px-4 py-2.5
                  text-sm text-gray-700
                  transition-colors duration-200
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
                disabled={operatorsLoading}
              >
                <option value="">Select an operator...</option>
                {operators?.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.fullName} ({op.employeeId})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (analyticsLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="space-y-6">
        {/* Operator Selector */}
        <OperatorSelector
          operators={operators ?? []}
          selectedId={operatorId}
          onChange={onSelectOperator}
          isLoading={operatorsLoading}
        />

        <div className="flex h-64 flex-col items-center justify-center text-center">
          <AlertCircle size={48} className="text-gray-300" />
          <p className="mt-4 text-sm font-medium text-gray-900">No analytics data available</p>
          <p className="mt-1 text-sm text-gray-500">
            This operator has no assessments in the selected period.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Operator Selector */}
      <OperatorSelector
        operators={operators ?? []}
        selectedId={operatorId}
        onChange={onSelectOperator}
        isLoading={operatorsLoading}
      />

      {/* Operator Header */}
      <OperatorHeader data={analytics} />

      {/* Summary Stats */}
      <SummaryStats data={analytics} />

      {/* Progression Chart */}
      <ProgressionChart data={analytics.progressionData} />

      {/* SOP Breakdown */}
      <SopBreakdown data={analytics.sopBreakdown} />

      {/* Recent Assessments */}
      <RecentAssessments data={analytics.recentAssessments} />
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function OperatorSelector({
  operators,
  selectedId,
  onChange,
  isLoading,
}: {
  operators: Array<{ id: string; fullName: string; employeeId: string }>;
  selectedId: string | null;
  onChange: (id: string | null) => void;
  isLoading: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-sm font-medium text-gray-700">Operator:</label>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="
          rounded-lg border border-gray-200 bg-white
          px-4 py-2
          text-sm text-gray-700
          transition-colors duration-200
          focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
        "
        disabled={isLoading}
      >
        <option value="">Select an operator...</option>
        {operators.map((op) => (
          <option key={op.id} value={op.id}>
            {op.fullName} ({op.employeeId})
          </option>
        ))}
      </select>
    </div>
  );
}

function OperatorHeader({ data }: { data: OperatorAnalyticsData }) {
  const { operator } = data;

  return (
    <div className="rounded-xl border border-gray-100 bg-gradient-to-r from-guild-mint/20 to-white p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-guild-green/10">
            <User size={28} className="text-guild-green" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {operator.firstName} {operator.lastName}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="font-mono">{operator.employeeId}</span>
              {operator.departmentName && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="flex items-center gap-1">
                    <Building2 size={14} />
                    {operator.departmentName}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <Link
          to={`/operators/${operator.id}`}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-guild-green transition-colors hover:bg-guild-mint/30"
        >
          View Profile
        </Link>
      </div>
    </div>
  );
}

function SummaryStats({ data }: { data: OperatorAnalyticsData }) {
  const { summary } = data;

  const TrendIcon = ({ trend }: { trend: string }) => {
    switch (trend) {
      case 'improving':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
            <TrendingUp size={14} /> Improving
          </span>
        );
      case 'declining':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-red-600">
            <TrendingDown size={14} /> Declining
          </span>
        );
      case 'stable':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-gray-500">
            <Minus size={14} /> Stable
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-gray-400">
            <AlertCircle size={12} /> Insufficient data
          </span>
        );
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {/* Overall Score */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Overall</p>
        <p className={`mt-1 text-3xl font-bold ${getScoreColor(summary.overallScore, 'text')}`}>
          {summary.overallScore !== null ? summary.overallScore.toFixed(0) : '—'}
        </p>
        <div className="mt-2">
          <TrendIcon trend={summary.trend} />
        </div>
      </div>

      {/* Compliance */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Compliance</p>
        <p className={`mt-1 text-3xl font-bold ${getScoreColor(summary.complianceScore, 'text')}`}>
          {summary.complianceScore !== null ? summary.complianceScore.toFixed(0) : '—'}
        </p>
      </div>

      {/* Efficiency */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Efficiency</p>
        <p className={`mt-1 text-3xl font-bold ${getScoreColor(summary.efficiencyScore, 'text')}`}>
          {summary.efficiencyScore !== null ? summary.efficiencyScore.toFixed(0) : '—'}
        </p>
      </div>

      {/* Safety */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Safety</p>
        <p className={`mt-1 text-3xl font-bold ${getScoreColor(summary.safetyScore, 'text')}`}>
          {summary.safetyScore !== null ? summary.safetyScore.toFixed(0) : '—'}
        </p>
      </div>

      {/* Percentile */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Percentile</p>
        <div className="mt-1 flex items-baseline gap-1">
          <p className="text-3xl font-bold text-gray-900">
            {summary.percentileRank !== null ? summary.percentileRank : '—'}
          </p>
          {summary.percentileRank !== null && (
            <span className="text-sm text-gray-500">%</span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-400">
          {summary.totalAssessments} assessments
        </p>
      </div>
    </div>
  );
}

function ProgressionChart({
  data,
}: {
  data: OperatorAnalyticsData['progressionData'];
}) {
  if (!data || data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900">Score Progression</h3>
        <div className="mt-8 flex h-48 flex-col items-center justify-center text-center">
          <p className="text-sm text-gray-500">Not enough data points to show progression.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900">Score Progression</h3>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="operatorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00674a" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#00674a" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
              labelFormatter={(label) =>
                new Date(label as string).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              }
              formatter={(value) => [(value as number).toFixed(1), 'Score']}
            />
            <Area
              type="monotone"
              dataKey="overall"
              stroke="#00674a"
              strokeWidth={2}
              fill="url(#operatorGradient)"
              dot={{ r: 4, fill: '#00674a' }}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SopBreakdown({ data }: { data: OperatorAnalyticsData['sopBreakdown'] }) {
  const TrendIcon = ({ trend }: { trend: string }) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp size={14} className="text-emerald-500" />;
      case 'declining':
        return <TrendingDown size={14} className="text-red-500" />;
      case 'stable':
        return <Minus size={14} className="text-gray-400" />;
      default:
        return <AlertCircle size={12} className="text-gray-300" />;
    }
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-gray-100 px-6 py-4">
        <h3 className="text-base font-semibold text-gray-900">Performance by SOP</h3>
      </div>
      <div className="divide-y divide-gray-50">
        {data.map((sop) => (
          <div
            key={sop.sopId}
            className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-gray-50/50"
          >
            <div className="flex-1">
              <Link
                to={`/sops/${sop.sopId}`}
                className="font-medium text-gray-900 transition-colors hover:text-guild-green"
              >
                {sop.sopTitle}
              </Link>
              <p className="mt-0.5 text-xs text-gray-500">
                {sop.assessmentCount} assessment{sop.assessmentCount !== 1 ? 's' : ''}
                {sop.lastAssessedAt && (
                  <>
                    {' '}
                    &middot; Last:{' '}
                    {new Date(sop.lastAssessedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <TrendIcon trend={sop.trend} />
              <span
                className={`
                  inline-flex min-w-[48px] items-center justify-center rounded-lg px-2.5 py-1
                  text-sm font-semibold
                  ${getScoreColor(sop.averageScore, 'badge')}
                `}
              >
                {sop.averageScore !== null ? sop.averageScore.toFixed(0) : '—'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentAssessments({ data }: { data: OperatorAnalyticsData['recentAssessments'] }) {
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={18} className="text-gray-400" />
          <h3 className="text-base font-semibold text-gray-900">Recent Assessments</h3>
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {data.slice(0, 5).map((assessment) => (
          <Link
            key={assessment.id}
            to={`/assessments/${assessment.id}`}
            className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-gray-50/50"
          >
            <div>
              <p className="font-medium text-gray-900">{assessment.sopTitle}</p>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`
                    inline-flex rounded-full px-2 py-0.5 text-xs font-semibold
                    ${
                      assessment.assessmentType === 'auto'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-purple-50 text-purple-700'
                    }
                  `}
                >
                  {assessment.assessmentType === 'auto' ? 'Auto' : 'Manual'}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(assessment.assessedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
            <span
              className={`
                inline-flex min-w-[48px] items-center justify-center rounded-lg px-2.5 py-1
                text-sm font-bold
                ${getScoreColor(assessment.overallScore, 'badge')}
              `}
            >
              {assessment.overallScore.toFixed(0)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default OperatorAnalytics;
